import { useState, useEffect, useRef } from "react"
import PubSub from "pubsub-js"
import { api } from "aldoo-ra/API"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Spinner from "aldoo-ra/Spinner"
import { ChevronDownIcon } from "@heroicons/react/24/outline"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { store } from "aldoo-ra/Store"
import { getStoreAccount, getPricingData } from "aldoo-ra/Store/api"
import Localization from "aldoo-ra/Localization"
import StorageCard from "@components/StorageCard"
import SecondaryPricePlan from "./SecondaryPricePlan"
import FAQ from "@components/FAQ"
import BillingToggle from "./BillingToggle"
import PlanCard from "./PlanCard"
import EnterpriseCard from "./EnterpriseCard"
import FeatureTable from "./FeatureTable"
import MobileFeatureTable from "./MobileFeatureTable"
import MainBackground from "@components/MainBackground"

const transferAPI = api("aldoo-transfer/pricing")

export default function Pricing() {
  const texts = TextBundle({ id: "transfer_pricing" })
  const [billing, setBilling] = useState("annual")
  const [storeData, setStoreData] = useState({ products: {}, prices: {} })
  const [activePid, setActivePid] = useState(null)
  const [plans, setPlans] = useState(null)
  const [features, setFeatures] = useState(null)
  const [categories, setCategories] = useState(null)
  const [creditPacks, setCreditPacks] = useState(null)
  const [matrixData, setMatrixData] = useState(null)
  const [comparePlansOpen, setComparePlansOpen] = useState(true)
  const [pricingLoading, setPricingLoading] = useState(true)
  const { currentLanguage } = Localization()

  useEffect(() => {
    if (!currentLanguage) return
    setPricingLoading(true)
    getPricingData(currentLanguage).then((res) => {
      if (res?.result) {
        setPlans(res.plans || [])
        setFeatures(res.features || [])
        setCategories(res.categories || [])
        setCreditPacks(res.creditPacks || [])
        setMatrixData(res.matrix ? [res.matrix] : [])
      }
      setPricingLoading(false)
    }).catch(() => setPricingLoading(false))
  }, [currentLanguage])

  useEffect(() => {
    transferAPI({ pricing: true })
      .then((data) => {
        if (!data?.products) return
        setStoreData({ products: data.products, prices: data.prices || {} })
      })
      .catch(() => {})

    getStoreAccount()
      .then((res) => {
        const account = res?.result || res
        const subs = account?.subscriptions || []
        const active = subs.find((s) => s.pid?.startsWith("transfer-plan-") && !s.pid?.includes("enterprise") && s.status === "active")
        if (active) setActivePid(active.pid)
      })
      .catch(() => {})
  }, [])

  const loading = pricingLoading

  useEffect(() => {
    if (loading) return
    const scrollToHash = () => {
      const hash = window.location.hash?.slice(1)
      if (!hash) return
      const el = document.getElementById(hash)
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" })
    }
    scrollToHash()
    window.addEventListener("hashchange", scrollToHash)
    return () => window.removeEventListener("hashchange", scrollToHash)
  }, [loading])

  const matrixPlanIds = matrixData?.[0]?.plans?.map((p) => (typeof p === "string" ? p : p?._id)) || []
  const mainPlans = matrixPlanIds.length > 0
    ? matrixPlanIds.map((id) => plans?.find((p) => p._id === id)).filter(Boolean)
    : plans?.filter((p) => p.plan_category !== "storage") || []
  const storagePlans = plans?.filter((p) => p.plan_category === "storage") || []

  // GA4 view_item_list — fire once when the pricing page has resolved both
  // the plan list AND store prices, so items carry real price + currency.
  // GA4's funnel report treats this as the entry point; without it
  // `add_to_cart` is the first event the user appears to fire and drop-off
  // attribution is skewed.
  const viewItemListFiredRef = useRef(false)
  useEffect(() => {
    if (viewItemListFiredRef.current) return
    if (loading) return
    if (!storeData?.products || !storeData?.prices) return
    const allPlans = [...mainPlans, ...storagePlans]
    if (!allPlans.length) return

    let currency = "USD"
    const items = []
    for (const plan of allPlans) {
      const productId = typeof plan.product === "string" ? plan.product : (plan.product?.item || plan.product?._id)
      const product = storeData.products?.[productId]
      const priceId = product?.price?._id || product?.price || product?.meta?.prices?.monthly
      const storePrice = storeData.prices?.[priceId]
      const model = storePrice?.price_model?.find((m) => m.phases?.[0]?.interval === (billing === "annual" ? "year" : "month"))
        ?? storePrice?.price_model?.[0]
      if (model?.currency) currency = model.currency
      items.push({
        item_id: product?.pid || plan?.id || plan?._id,
        item_name: product?.name || plan?.name || plan?.id,
        price: model?.amount ?? 0,
        quantity: 1,
      })
    }
    if (!items.length) return
    PubSub.publish("Tracking_ViewItemList", {
      items,
      currency,
      itemListName: "Pricing",
    })
    viewItemListFiredRef.current = true
  }, [loading, storeData, mainPlans, storagePlans, billing])

  const TOP_PLAN_IDS = ["free", "pro"]
  const topPlans = mainPlans.filter((p) => TOP_PLAN_IDS.includes(p.id))
  const enterprisePlans = mainPlans.filter((p) => p.id === "enterprise")
  const secondaryPlans = mainPlans.filter(
    (p) => !TOP_PLAN_IDS.includes(p.id) && p.id !== "enterprise",
  )

  // the page's heading, in the server HTML too (the plans load in the browser)
  const header = (
    <div className="text-center mb-8">
      <Typography
        component="h1"
        variant="h1"
        className="text-text mb-6"
      >
        {texts?.choosePlan}
      </Typography>

      <Typography
        variant="body1"
        className="max-w-2xl mx-auto"
      >
        {texts?.choosePlanSubtitle}
      </Typography>
    </div>
  )

  if (loading) {
    return (
      <div className="min-h-screen bg-canvas pt-28 sm:pt-36 md:pt-40 px-4">
        {header}
        <div className="flex items-center justify-center pt-10">
          <Spinner variant="site-loading" />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      <div className="relative -mt-16 rounded-b-[30px] sm:rounded-b-[50px] md:rounded-b-[75px] xl:rounded-b-[100px] overflow-hidden">
        <MainBackground />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 sm:pt-36 md:pt-40 pb-10 sm:pb-16 md:pb-28">
          {/* Header */}
          {header}

          <BillingToggle
            billing={billing}
            setBilling={setBilling}
            texts={texts}
          />

          {(topPlans.length > 0 || enterprisePlans.length > 0) && (
            <div id="plans" className="w-full max-w-[340px] md:max-w-[720px] grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 mx-auto">
              {topPlans.map((plan) => (
                // Mobile order: Pro on top, Free below. Desktop keeps the
                // grid's natural left-to-right order (Free, Pro). Tailwind
                // `order-*` only applies in single-column on mobile; on md+
                // the grid flow takes over again.
                <div
                  key={plan._id}
                  className={
                    plan.id === "pro" ? "order-1 md:order-none"
                    : plan.id === "free" ? "order-2 md:order-none"
                    : "md:order-none"
                  }
                >
                  <PlanCard
                    plan={plan}
                    billing={billing}
                    isPopular={!!plan.is_popular}
                    storeData={storeData}
                    texts={texts}
                    activePid={activePid}
                    features={features}
                  />
                </div>
              ))}

              {enterprisePlans.map((plan) => (
                <EnterpriseCard key={plan._id} plan={plan} storeData={storeData} texts={texts} features={features} />
              ))}
            </div>
          )}

          {secondaryPlans.length > 0 && (
            <div className="w-full max-w-[340px] md:max-w-[720px] flex flex-col gap-4 mx-auto">
              {secondaryPlans.map((plan) => (
                <SecondaryPricePlan
                  key={plan._id}
                  plan={plan}
                  billing={billing}
                  storeData={storeData}
                  texts={texts}
                  activePid={activePid}
                  features={features}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Compare section*/}
      <section className="w-full px-4 sm:px-6 lg:px-8 py-16 md:py-20 lg:my-[100px]">
        {features?.length > 0 && categories?.length > 0 && mainPlans.length > 0 && (
          <div className="max-w-7xl mx-auto ">
            <Button
              variant="primary"
              onClick={() => setComparePlansOpen((v) => !v)}
              className="flex items-center gap-2 mx-auto !h-auto px-8 py-4"
            >
              <span className="text-2xl font-bold">
                {texts?.comparePlans}
              </span>
              <ChevronDownIcon className={`w-5 h-5 shrink-0 transition-transform duration-300 ${comparePlansOpen ? "rotate-180" : ""}`} />
            </Button>

            {comparePlansOpen && (
              <div className="">
                <div className="hidden md:block">
                  <FeatureTable plans={mainPlans} features={features} categories={categories} texts={texts} storeData={storeData} billing={billing} />
                </div>
                <div className="md:hidden mt-5">
                  <MobileFeatureTable plans={mainPlans} features={features} categories={categories} texts={texts} storeData={storeData} activePid={activePid} billing={billing} />
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* FAQ */}
      <FAQ />
    </div>
  )
}